import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { fitPills } from './fitPills';

/** Matches the pills track's own `gap-1.5`. */
const GAP_PX = 6;

export interface FitPillsHandle {
  /** Ref for the pills track itself — its `clientWidth` is the budget. */
  containerRef: (node: HTMLElement | null) => void;
  /** Ref for the always-rendered, invisible measuring copy of the "…" pill. */
  moreMeasureRef: (node: HTMLElement | null) => void;
  /** Ref-callback factory, one per pill, in order — including the leading "All objectives" pill. */
  itemRef: (index: number) => (node: HTMLElement | null) => void;
  /** How many pills (from the front) stay on the line. */
  visibleCount: number;
  /** Whether the "…" trigger is needed. */
  overflowed: boolean;
}

/**
 * Measures the controls row's pills and decides how many fit, refitting on resize. The decision
 * itself lives in `fitPills.ts`, tested on its own; this just wires real DOM widths into it.
 *
 * The "…" pill's own width has to be known *before* the fit decision can be made, so an invisible
 * copy of it (`moreMeasureRef`) is always mounted and measured alongside the real pills, whether or
 * not overflow ends up needed — the same reservation the visual pick describes: the last visible pill
 * must never end up sitting half under the menu it made necessary.
 *
 * Unverified in a browser (see the builder's handoff note) — this is the piece of the surface most
 * worth checking by hand once the page renders somewhere reachable.
 */
export const useFitPills = (itemCount: number, signature: string): FitPillsHandle => {
  const containerNode = useRef<HTMLElement | null>(null);
  const moreMeasureNode = useRef<HTMLElement | null>(null);
  const itemNodes = useRef<(HTMLElement | null)[]>([]);
  const [result, setResult] = useState({ visibleCount: itemCount, overflowed: false });

  const measure = useCallback(() => {
    // `signature` (each pill's key + label, joined) is not read for its value — it exists only to
    // change this callback's identity when the pills reorder or relabel without `itemCount`
    // changing. Re-picking the selected objective sorts it to the front, and an objective title
    // resolving from a placeholder to its real name both leave `itemCount` untouched, but the DOM
    // nodes behind `itemRef`'s indices now mean something else. Without this, the effect below
    // never re-fires, and a pill that inherited a previous pass's `display: none` from a different
    // index stays hidden even once it has moved into view.
    void signature;

    const container = containerNode.current;
    if (!container) return;

    const nodes = itemNodes.current.slice(0, itemCount);
    // A pill the previous pass hid measures as zero width, and the row could never win it back on a
    // later, wider measurement — so every pill is forced visible before this pass reads any width,
    // the same "unhide everything, then measure" order the prototype's fitPills() uses.
    for (const node of nodes) node?.style.removeProperty('display');

    const widths = nodes.map((node) => node?.getBoundingClientRect().width ?? 0);
    const moreWidth = (moreMeasureNode.current?.getBoundingClientRect().width ?? 0) + GAP_PX;
    const next = fitPills(widths, container.getBoundingClientRect().width, GAP_PX, moreWidth);

    nodes.forEach((node, index) => {
      if (node) node.style.display = index < next.visibleCount ? '' : 'none';
    });
    setResult(next);
  }, [itemCount, signature]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    const container = containerNode.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  return {
    containerRef: (node) => {
      containerNode.current = node;
    },
    moreMeasureRef: (node) => {
      moreMeasureNode.current = node;
    },
    itemRef: (index) => (node) => {
      itemNodes.current[index] = node;
    },
    visibleCount: Math.min(result.visibleCount, itemCount),
    overflowed: result.overflowed,
  };
};
